import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Calendar, MapPin, Users, Newspaper, TrendingUp, Image, ArrowRight, CheckCircle, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PastEventsSection = () => {
  const [pastEvents, setPastEvents] = useState([]);
  const [eventGallery, setEventGallery] = useState({});

  // Default placeholder images for each event
  const defaultImages = {
    "hyderabad-2024": [
      "https://images.unsplash.com/photo-1552674605-db6ffd4facb5?w=600",
      "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=600",
      "https://images.unsplash.com/photo-1513593771513-7b58b6c4af38?w=600"
    ],
    "bengaluru-2025": [
      "https://images.unsplash.com/photo-1461897104016-0b3b00b4f9a9?w=600",
      "https://images.unsplash.com/photo-1532444458054-01a7dd3e9fca?w=600",
      "https://images.unsplash.com/photo-1594882645126-14020914d58d?w=600"
    ]
  };

  useEffect(() => {
    const fetchPastEvents = async () => {
      try {
        const response = await axios.get(`${API}/past-events`);
        setPastEvents(response.data.past_events);
      } catch (e) {
        console.error("Error fetching past events:", e);
        setPastEvents([
          {
            id: "hyderabad-2024",
            name: "The FinRun Hyderabad 2024",
            edition: "1st Edition",
            date: "December 2024",
            location: "Gachibowli Stadium, Hyderabad",
            description: "The inaugural edition that launched India's cyber awareness running movement. Supported by Government of Telangana and Department of Cyber Crimes.",
            participants: "3,000+",
            highlights: ["First-ever cyber awareness run in India", "Government of Telangana support", "Department of Cyber Crimes partnership"],
            impact: { participants: "3,000+", awareness_reach: "500,000+", media_coverage: "50+ outlets", social_impressions: "2M+" },
            external_gallery_url: "https://drive.google.com/drive/folders/1TsE4NN-TsrgXzVPhFrdf0wZzf1gl0SDF"
          },
          {
            id: "bengaluru-2025",
            name: "The FinRun Bengaluru 2025",
            edition: "2nd Edition",
            date: "June 15, 2025",
            location: "St. Joseph's PU College Grounds, Bengaluru",
            description: "The second edition brought cyber awareness to India's tech capital, engaging thousands of IT professionals and students.",
            participants: "5,000+",
            highlights: ["Expanded to India's Silicon Valley", "Strong participation from tech community", "Corporate partnerships"],
            impact: { participants: "5,000+", awareness_reach: "1,000,000+", media_coverage: "75+ outlets", social_impressions: "5M+" },
            external_gallery_url: "https://bhaagoindia.com/events/the-finrun-2025-5668/memories/"
          }
        ]);
      }
    };
    fetchPastEvents();
  }, []);

  // Fetch gallery items for each event
  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const [hyd, blr] = await Promise.all([
          axios.get(`${API}/gallery/hyderabad-2024`),
          axios.get(`${API}/gallery/bengaluru-2025`)
        ]);
        
        const isValidImageUrl = (url) => url && /\.(jpg|jpeg|png|gif|webp|svg)($|\?)/i.test(url);
        
        const hydImages = (hyd.data.items || []).filter(item => item.media_type === "image" && isValidImageUrl(item.url)).map(item => item.url);
        const blrImages = (blr.data.items || []).filter(item => item.media_type === "image" && isValidImageUrl(item.url)).map(item => item.url);
        
        setEventGallery({
          "hyderabad-2024": hydImages.length > 0 ? hydImages : defaultImages["hyderabad-2024"],
          "bengaluru-2025": blrImages.length > 0 ? blrImages : defaultImages["bengaluru-2025"]
        });
      } catch (e) {
        setEventGallery(defaultImages);
      }
    };
    fetchGalleries();
  }, []);

  const getEventImages = (eventId) => eventGallery[eventId] || defaultImages[eventId] || [];

  // Single Event Card Component
  const EventCard = ({ event, reverse = false }) => (
    <div className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-start ${reverse ? 'lg:flex-row-reverse' : ''}`}>
      {/* Left - Event Details */}
      <div className={reverse ? 'lg:order-2' : ''}>
        <div className="inline-flex items-center gap-2 bg-[#FFD700]/20 text-[#B8860B] rounded-full px-4 py-1.5 mb-4">
          <CheckCircle className="w-4 h-4" />
          <span className="text-sm font-bold uppercase tracking-wider">{event.edition}</span>
        </div>
        
        <h3 className="text-2xl md:text-3xl font-bold text-[#0A0A0A] font-['Outfit'] mb-3">
          {event.name}
        </h3>
        
        <div className="flex flex-wrap items-center gap-4 mb-4 text-[#4B5563] text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#FF3B30]" />
            <span>{event.date}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#007AFF]" />
            <span>{event.location}</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#FFD700]" />
            <span>{event.participants} Runners</span>
          </div>
        </div>
        
        <p className="text-[#4B5563] mb-6 leading-relaxed">
          {event.description}
        </p>

        {/* Impact Stats - Compact */}
        <div className="grid grid-cols-4 gap-2 mb-6">
          <div className="bg-[#111827] rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-white">{event.impact?.participants}</p>
            <p className="text-[10px] text-gray-400">Runners</p>
          </div>
          <div className="bg-[#111827] rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-[#FFD700]">{event.impact?.awareness_reach}</p>
            <p className="text-[10px] text-gray-400">Reached</p>
          </div>
          <div className="bg-[#111827] rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-[#007AFF]">{event.impact?.media_coverage}</p>
            <p className="text-[10px] text-gray-400">Media</p>
          </div>
          <div className="bg-[#111827] rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-[#00C853]">{event.impact?.social_impressions}</p>
            <p className="text-[10px] text-gray-400">Impressions</p>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-wrap gap-3">
          <Link to={`/gallery/${event.id}`}>
            <Button className="bg-[#FF3B30] hover:bg-[#FF1C0F] text-white rounded-full">
              <Image className="w-4 h-4 mr-2" />
              View Gallery
            </Button>
          </Link>
          {event.external_gallery_url && (
            <a href={event.external_gallery_url} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-full border-[#007AFF] text-[#007AFF] hover:bg-[#007AFF] hover:text-white">
                <ExternalLink className="w-4 h-4 mr-2" />
                All Photos
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* Right - Gallery Preview */}
      <div className={reverse ? 'lg:order-1' : ''}>
        {/* Main Image */}
        <div className="relative rounded-2xl overflow-hidden shadow-xl mb-3 group">
          <img 
            src={getEventImages(event.id)?.[0]}
            alt={event.name}
            className="w-full h-[280px] object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-4 left-4 text-white">
            <p className="text-sm opacity-80">{event.edition}</p>
            <p className="font-bold">{event.participants} Participants</p>
          </div>
        </div>
        
        {/* Thumbnail Grid */}
        <div className="grid grid-cols-4 gap-2">
          {getEventImages(event.id).slice(1, 5).map((img) => (
            <div key={img} className="relative rounded-lg overflow-hidden aspect-square group cursor-pointer">
              <img 
                src={img}
                alt={event.name}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <section id="past-events" className="section-spacing bg-white" data-testid="past-events-section">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#FFD700] text-sm uppercase tracking-[0.2em] font-bold mb-4">
            Our Journey
          </p>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#0A0A0A] font-['Outfit'] mb-4">
            Past <span className="text-[#FF3B30]">Editions</span>
          </h2>
          <p className="text-[#4B5563] text-lg max-w-2xl mx-auto">
            Relive the moments that made The FinRun a national movement.
          </p>
        </div>

        {/* Both Events - Vertical Layout */}
        <div className="space-y-20">
          {pastEvents.map((event, index) => (
            <EventCard key={event.id} event={event} reverse={index % 2 === 1} />
          ))}
        </div>

        {/* Total Impact Summary */}
        <div className="mt-20 bg-gradient-to-r from-[#FF3B30] to-[#FF6B35] rounded-3xl p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="text-white">
              <h3 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-4">
                The Movement Grows Stronger
              </h3>
              <p className="text-white/80 text-lg mb-6">
                From Hyderabad to Bengaluru, and now Chennai - The FinRun has touched thousands of lives, 
                spreading the message of cyber safety across India.
              </p>
              <Link to="/register">
                <Button 
                  className="bg-white text-[#FF3B30] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold"
                  data-testid="join-movement-btn"
                >
                  Be Part of Chennai 2026
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 text-white">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <p className="text-4xl md:text-5xl font-black font-['Outfit']">8,000+</p>
                <p className="text-sm opacity-80">Total Runners</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <p className="text-4xl md:text-5xl font-black font-['Outfit']">2</p>
                <p className="text-sm opacity-80">Cities Covered</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <p className="text-4xl md:text-5xl font-black font-['Outfit']">1.5M+</p>
                <p className="text-sm opacity-80">People Reached</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <p className="text-4xl md:text-5xl font-black font-['Outfit']">7M+</p>
                <p className="text-sm opacity-80">Social Impressions</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PastEventsSection;
